<template>
  <section class="related-resources">
    <SectionTitle title="关联资源" />

    <div class="related-resources__main">
      <div class="related-resources__toolbar">
        <span class="related-resources__total">
          共检索到 <em>{{ total }}</em> 条结果
        </span>
        <div class="related-resources__view">
          <button
            type="button"
            :class="{ 'is-active': viewMode === 'list' }"
            @click="viewMode = 'list'"
          >
            <i class="el-icon-s-operation" />
          </button>
          <button
            type="button"
            :class="{ 'is-active': viewMode === 'grid' }"
            @click="viewMode = 'grid'"
          >
            <i class="el-icon-menu" />
          </button>
        </div>
      </div>

      <div v-if="loading" class="related-resources__loading">加载中...</div>

      <div
        v-else
        class="related-resources__list"
        :class="{ 'related-resources__list--grid': viewMode === 'grid' }"
      >
        <article
          v-for="item in list"
          :key="item.id"
          class="resource-card"
          @click="goDetail(item)"
        >
          <div class="resource-card__thumb">
            <img
              v-if="item.image"
              :src="item.image"
              :alt="item.title"
            />
            <div v-else class="resource-card__thumb-placeholder">
              <span>📖</span>
              <span>暂无封面</span>
            </div>
          </div>
          <div class="resource-card__body">
            <h4 class="resource-card__title">{{ item.title }}</h4>
            <p v-if="item.abstract" class="resource-card__summary">{{ item.abstract }}</p>
            <div class="resource-card__meta">
              <p v-if="item.resourceTypeLabel">
                <span>资源类型：</span>{{ item.resourceTypeLabel }}
              </p>
              <p
                v-for="field in displayAttributes(item)"
                :key="field.id || field.label"
              >
                <span>{{ field.label }}：</span>{{ field.value || '-' }}
              </p>
            </div>
          </div>
        </article>
      </div>

      <div v-if="total > pageSize" class="related-resources__pagination">
        <el-pagination
          :current-page.sync="innerPage"
          :page-size="pageSize"
          :total="total"
          layout="prev, pager, next"
          background
          @current-change="handlePageChange"
        />
      </div>
    </div>
  </section>
</template>

<script>
import SectionTitle from './SectionTitle.vue'
import { isArchiveResource } from '@/utils/resourceAccess'
import { mapGetters } from 'vuex'

export default {
  name: 'RelatedResources',
  components: { SectionTitle },
  props: {
    list: { type: Array, default: () => [] },
    total: { type: Number, default: 0 },
    loading: { type: Boolean, default: false },
    page: { type: Number, default: 1 },
    pageSize: { type: Number, default: 10 }
  },
  data() {
    return {
      viewMode: 'list',
      innerPage: this.page
    }
  },
  computed: {
    ...mapGetters(['isLoggedIn'])
  },
  watch: {
    page(value) {
      this.innerPage = value
    }
  },
  methods: {
    displayAttributes(item) {
      const limit = this.viewMode === 'grid' ? 3 : 4
      return (item.attributes || []).slice(0, limit)
    },
    handlePageChange(page) {
      this.$emit('page-change', page)
    },
    goDetail(item) {
      if (!item || !item.id) {
        return
      }
      if (isArchiveResource(item) && !this.isLoggedIn) {
        this.$message.warning('档案类资源需登录后访问')
        return
      }
      this.$router.push(`/resources/${item.id}`)
    }
  }
}
</script>

<style lang="scss" scoped>
.related-resources {
  margin-bottom: $spacing-3xl;

  &__main {
    min-width: 0;
  }

  &__toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: $spacing-base;
  }

  &__total {
    font-size: rem(13);
    color: $color-text-light;

    em {
      font-style: normal;
      font-weight: 600;
      color: $color-brown-dark;
    }
  }

  &__view {
    display: flex;
    gap: $spacing-xs;

    button {
      width: 32px;
      height: 32px;
      border-radius: 4px;
      background: $color-white;
      border: 1px solid $color-border;
      color: $color-text-light;

      &.is-active {
        background: $color-brown-dark;
        border-color: $color-brown-dark;
        color: $color-white;
      }
    }
  }

  &__loading {
    padding: $spacing-xl 0;
    text-align: center;
    color: $color-text-light;
  }

  &__list {
    display: flex;
    flex-direction: column;
    gap: $spacing-base;

    &--grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    }
  }

  &__pagination {
    display: flex;
    justify-content: center;
    margin-top: $spacing-xl;
  }
}

.resource-card {
  display: flex;
  gap: $spacing-base;
  padding: $spacing-base;
  background: $color-white;
  border: 1px solid rgba($color-border, 0.4);
  border-radius: 4px;
  cursor: pointer;
  transition: box-shadow 0.2s;

  &:hover {
    box-shadow: 0 4px 16px rgba($color-brown-dark, 0.08);
  }

  &__thumb {
    width: 140px;
    height: 100px;
    flex-shrink: 0;
    border-radius: 4px;
    overflow: hidden;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      filter: grayscale(0.4) sepia(0.15);
    }
  }

  &__thumb-placeholder {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 4px;
    font-size: rem(12);
    color: $color-text-light;
    background: $color-paper-light;

    span:first-child {
      font-size: rem(24);
      opacity: 0.6;
    }
  }

  &__body {
    flex: 1;
    min-width: 0;
  }

  &__title {
    font-family: $font-serif;
    font-size: rem(16);
    font-weight: 700;
    color: $color-brown-dark;
    margin-bottom: $spacing-xs;
  }

  &__summary {
    font-size: rem(13);
    line-height: 1.7;
    color: $color-text-light;
    margin-bottom: $spacing-xs;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  &__meta {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 2px $spacing-base;
    font-size: rem(13);
    color: $color-text-light;

    span {
      color: $color-text;
    }
  }
}

.related-resources__list--grid .resource-card {
  flex-direction: column;

  .resource-card__thumb {
    width: 100%;
    height: 160px;
  }

  .resource-card__meta {
    grid-template-columns: 1fr;
  }
}
</style>
